import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RiAddLine } from '@remixicon/react';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import {
  applyDefaultWorkflows,
  applyLaneEndpoint,
  driverLabel,
  laneMatchKey,
  makeStops,
  prefillEndpoints,
  shipmentStopIntent,
  shipmentStopsFromPlan,
  syncLinkedStops,
  type StopDraft,
} from '@/pages/shipments/components/direct4wStops';
import RouteStopsMap, {
  type RouteStopPoint,
} from '@/pages/shipments/components/RouteStopsMap';
import Direct4WRiderStep, {
  type RiderValue,
} from '@/pages/shipments/components/steps/Direct4WRiderStep';
import Direct4WStopsStep from '@/pages/shipments/components/steps/Direct4WStopsStep';
import { resolveClientNoteFields } from '@/pages/shipments/components/clientNoteFields';
import {
  findUniqueDriverByName,
  riderDisplayName,
  riderPhoneOf,
} from '@/pages/shipments/components/steps/riderLookup';
import { useDirect4WWorkflows } from '@/pages/shipments/components/steps/useDirect4WWorkflows';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { DatePicker } from '@/design-system/date-picker';
import { toast } from '@/design-system/toaster';
import StepIndicator from '@/components/StepIndicator/StepIndicator';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { useDriversDropdown } from '@/pages/scheduled-instant/components/useDriversDropdown';
import { addressesAPI } from '@/services/api/addresses';
import {
  routePlansAPI,
  workflowPinProblemLabel,
  type RoutePlan,
} from '@/services/api/routePlans';
import { ujpAPI } from '@/services/api/ujp';
import {
  CreateDirect4WPayload,
  Direct4WDeliveryType,
  shipmentsAPI,
  ShipmentExim,
} from '@/services/api/shipments';

interface CreateShipment4WModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful create so the list can refetch. */
  onCreated: () => void;
}

// ── Static option sets ────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Tipe & info' },
  { id: 2, label: 'Rute & stops' },
  { id: 3, label: 'Review' },
];

const DELIVERY_TYPES: { value: Direct4WDeliveryType; label: string }[] = [
  { value: 'SINGLE_DROP', label: 'Single Drop' },
  { value: 'MULTI_DROP', label: 'Multi Drop' },
  { value: 'MULTI_PICKUP', label: 'Multi Pickup' },
];

// EXIM direction — drives which required additional-note fields apply and the
// server's default-workflow resolution. GENERAL = non-EXIM / umum.
const DIRECTION_OPTIONS: { value: ShipmentExim; label: string }[] = [
  { value: 'EXPORT', label: 'Export' },
  { value: 'IMPORT', label: 'Import' },
  { value: 'GENERAL', label: 'Umum' },
];

// Container tag — the label is exactly the PRD wording; the value is the
// machine token sent to the BE.
const CONTAINER_OPTIONS: { value: string; label: string }[] = [
  { value: 'FCL_20FT', label: 'FCL 20ft' },
  { value: 'FCL_40FT', label: 'FCL 40ft' },
  { value: 'LAINNYA', label: 'Lainnya' },
  { value: 'NON_CONTAINER', label: 'Tidak ada / Non-kontainer' },
];

const toISODate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

// Greyed provenance styling — "field abu-abu = dari UJP" but still editable.
const GREY_FIELD = 'bg-bg-weak-50 border-dashed';

// ── UJP CSV import ──────────────────────────────────────────────────────────────
// The approved UJP (Running Cost) request is exported as a CSV whose columns are:
//   order_no,tgl_delivery,tipe_pengiriman,tipe_layanan,origin,destination,km,
//   plat_nomor,jenis_unit,driver,shift
// One row = one shipment. We parse it client-side and prefill Step-1 (client is
// NOT in the export, so it stays a manual pick; coordinates come from the stop
// PlacesAutocomplete since the CSV carries addresses only).
const UJP_REQUIRED_HEADERS = ['order_no', 'origin', 'destination'];

/** Minimal RFC-4180-ish CSV parser (handles quoted fields with commas). */
const parseCsv = (text: string): Record<string, string>[] => {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  const pushRow = () => {
    row.push(field);
    field = '';
    if (row.some((v) => v.trim() !== '')) rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      pushRow();
    } else field += c;
  }
  if (field !== '' || row.length) pushRow();
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] ?? '').trim();
    });
    return obj;
  });
};

/** UJP dates are `dd/mm/yyyy`. */
const parseUjpDate = (s: string): Date | undefined => {
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return undefined;
  const dt = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(dt.getTime()) ? undefined : dt;
};

/** `tipe_pengiriman` is Single | Multi; Multi defaults to Multi Drop (editable). */
const mapUjpDeliveryType = (s: string): Direct4WDeliveryType =>
  s.trim().toLowerCase().startsWith('multi') ? 'MULTI_DROP' : 'SINGLE_DROP';

const CreateShipment4WModal: React.FC<CreateShipment4WModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [step, setStep] = useState<number>(1);

  // The modal body keeps its scroll offset across a step change, so advancing
  // from a long Step 2 lands the user mid-page on Step 3 — past fields they
  // still have to review. Reset to the top on every step change.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [step]);

  // Source gate — either import an approved UJP CSV (prefills the fields) or
  // toggle manual entry. Both unlock "Lanjut".
  const [importedFromUjp, setImportedFromUjp] = useState(false);
  const [ujpFileName, setUjpFileName] = useState('');
  const ujpInputRef = useRef<HTMLInputElement>(null);
  const [manualMode, setManualMode] = useState(false);

  // Step-1 fields
  const [clientID, setClientID] = useState<number | null>(null);
  const [bookingId, setBookingId] = useState('');
  const [deliveryType, setDeliveryType] =
    useState<Direct4WDeliveryType>('SINGLE_DROP');
  const [containerType, setContainerType] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);

  // EXIM direction + its dynamic recon fields. `additionalNotes` is keyed by the
  // definition's server-generated `key`; only the fields shown for the current
  // direction are kept, so switching direction never smuggles the other lane's
  // values into the payload.
  const [exim, setExim] = useState<ShipmentExim>('GENERAL');
  const [additionalNotes, setAdditionalNotes] = useState<
    Record<string, string>
  >({});
  // Ad-hoc notes ops types on the spot (no seeded definition needed) — free
  // variable name + value, stored in the same additional_notes jsonb. The BE
  // freezes each custom key with the name as its own label (bounded: ≤ 20 keys).
  const [customNotes, setCustomNotes] = useState<
    { id: string; name: string; value: string }[]
  >([]);

  // Rider — REQUIRED by the BE (assigns the route's executor). No rider-list
  // API exists in this repo, so the ID is entered manually until UJP sends it.
  // Driver name is reused from `route.driverName`; phone is optional.
  const [riderId, setRiderId] = useState('');
  // Mitra code of the picked driver — frozen onto the route so ops can filter
  // and read "Driver code" on the detail (it was never sent before).
  const [riderCode, setRiderCode] = useState('');
  const [riderPhone, setRiderPhone] = useState('');

  // Route / vehicle-driver block ("dari UJP" — greyed but editable).
  const [route, setRoute] = useState({
    originName: '',
    destinationName: '',
    km: '',
    plateNumber: '',
    unitType: '',
    driverName: '',
    serviceType: '',
    shift: '',
  });

  // Step-2 stops
  const [stops, setStops] = useState<StopDraft[]>(() =>
    makeStops('SINGLE_DROP')
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const providers = useProvidersDropdown(open);
  // Rider directory — GET /v3/drivers (assignable statuses), searched by name/code/phone.
  // A 4W trip needs a truck DRIVER. The directory also holds 2W RIDERs,
  // warehouse staff and canvassers, and every one of them was selectable here
  // — nothing downstream rejects a courier assigned to a tronton.
  const riders = useDriversDropdown(open, { type: 'DRIVER' });

  // Workflow pickers — REAL endpoint (stopWorkflowsAPI.list), scoped to the
  // selected client + ACTIVE, split by stop type; default preselection +
  // milestone previews. Shared with the UJP wizard through the steps hook.
  const { workflows, workflowsLoading, wfDetails, wfDetailErrors } =
    useDirect4WWorkflows({ open, clientID, exim, stops, setStops });

  // "Isi dari rute" (CR2-D7): the client's active route plans + its pool-leg
  // rule, so picking a plan fills the stops with the right set (all when
  // charged, else without the pool legs).
  const [routePlans, setRoutePlans] = useState<RoutePlan[]>([]);
  const [routePlansLoading, setRoutePlansLoading] = useState(false);
  const [chargedPositioning, setChargedPositioning] = useState(false);
  // CR-5: the plan the stops were last prefilled from — sent as a trace on the
  // create payload. Cleared whenever the stop set is rebuilt from scratch
  // (delivery-type switch, CSV import); a later per-stop edit keeps it, since
  // the plan is still where the route came from.
  const [routePlanId, setRoutePlanId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || clientID == null) {
      setRoutePlans([]);
      return;
    }
    let cancelled = false;
    setRoutePlansLoading(true);
    routePlansAPI
      .list({ clientId: clientID, active: true })
      .then((rows) => !cancelled && setRoutePlans(rows))
      .catch(() => !cancelled && setRoutePlans([]))
      .finally(() => !cancelled && setRoutePlansLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, clientID]);

  useEffect(() => {
    if (!open || clientID == null) {
      setChargedPositioning(false);
      return;
    }
    let cancelled = false;
    ujpAPI
      .listClientConfigs()
      .then((rows) => {
        if (cancelled) return;
        const row = rows.find((r) => r.clientId === clientID);
        setChargedPositioning(row?.chargedPositioning ?? false);
      })
      .catch(() => !cancelled && setChargedPositioning(false));
    return () => {
      cancelled = true;
    };
  }, [open, clientID]);

  const fillStopsFromPlan = (plan: RoutePlan) => {
    const fresh = shipmentStopsFromPlan(plan, chargedPositioning);
    setStops(applyDefaultWorkflows(syncLinkedStops(fresh), workflows, exim));
    setRoutePlanId(plan.id);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith('stop-')) delete next[k];
      });
      return next;
    });
  };

  // Additional-note fields for the picked client + direction, resolved on the FE
  // by client NAME (no per-client definitions table / endpoint). Synthetic
  // AdditionalFieldDefinition[] so the render + validation path is unchanged.
  const fieldDefs = useMemo(
    () =>
      resolveClientNoteFields(
        clientID != null ? providers.labelFor(clientID) : '',
        exim
      ),
    [providers, clientID, exim]
  );

  // Drop typed values whose field no longer applies (e.g. after a direction
  // switch), so the payload never smuggles a hidden field's value.
  useEffect(() => {
    const keep = new Set(fieldDefs.map((d) => d.key));
    setAdditionalNotes((prev) => {
      const entries = Object.entries(prev).filter(([k]) => keep.has(k));
      return entries.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(entries);
    });
  }, [fieldDefs]);

  // Reset everything on open.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setManualMode(false);
    setImportedFromUjp(false);
    setUjpFileName('');
    setClientID(null);
    setBookingId('');
    setDeliveryType('SINGLE_DROP');
    setContainerType('');
    setDeliveryDate(undefined);
    setExim('GENERAL');
    setAdditionalNotes({});
    setCustomNotes([]);
    setRiderId('');
    setRiderCode('');
    setRiderPhone('');
    setRoute({
      originName: '',
      destinationName: '',
      km: '',
      plateNumber: '',
      unitType: '',
      driverName: '',
      serviceType: '',
      shift: '',
    });
    setStops(makeStops('SINGLE_DROP'));
    setRoutePlanId(null);
    setErrors({});
    setIsSubmitting(false);
  }, [open]);

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const patchRoute = (patch: Partial<typeof route>) =>
    setRoute((prev) => ({ ...prev, ...patch }));

  // ── Ad-hoc custom notes (name → value) ──────────────────────────────────────
  const MAX_CUSTOM_NOTES = 20;
  const addCustomNote = () =>
    setCustomNotes((prev) =>
      prev.length >= MAX_CUSTOM_NOTES
        ? prev
        : [...prev, { id: `cn-${Date.now()}-${prev.length}`, name: '', value: '' }]
    );
  const updateCustomNote = (
    id: string,
    patch: Partial<{ name: string; value: string }>
  ) =>
    setCustomNotes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, ...patch } : n))
    );
  const removeCustomNote = (id: string) =>
    setCustomNotes((prev) => prev.filter((n) => n.id !== id));

  // Regenerating the stop set when the delivery type changes discards the
  // per-stop edits — that's intentional, the card count itself changes.
  const handleDeliveryTypeChange = (value: Direct4WDeliveryType) => {
    setDeliveryType(value);
    // Fresh stops start with no workflow, so re-apply the defaults the pickers
    // already loaded — otherwise changing the type silently empties them.
    //
    // A type change rebuilds the stop set from scratch (the card COUNT changes),
    // which would also throw away a UJP import. `route` still holds the imported
    // origin/destination across the switch, so re-apply the same name prefill
    // and re-run the master lookup — the pickup + customer addresses (with
    // coordinates) carry over instead of vanishing.
    const fresh = importedFromUjp
      ? prefillEndpoints(makeStops(value), route.originName, route.destinationName)
      : makeStops(value);
    setStops(applyDefaultWorkflows(syncLinkedStops(fresh), workflows, exim));
    setRoutePlanId(null);
    if (importedFromUjp) {
      void applyLaneFromUjp(route.originName, route.destinationName);
    }
    setErrors((prev) => {
      // Drop stop-scoped errors that no longer map to a card.
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (!k.startsWith('stop-')) next[k] = v;
      }
      return next;
    });
  };

  /**
   * Resolve the UJP's origin/destination NAMES against the address master and
   * fill both endpoints — coordinates included.
   *
   * The CSV carries names only, so a plain prefill leaves every stop without
   * coordinates, which Step-2 validation rejects; ops then pin-drop each stop
   * by hand. A lane already holds the confirmed point for both ends, and the
   * master dedups lanes by exactly this origin+destination name pair, so one
   * lookup fills the whole route (the container-return leg mirrors the origin).
   *
   * Best-effort: no match leaves the text prefill in place, exactly as before.
   */
  const applyLaneFromUjp = async (
    originName: string,
    destinationName: string
  ) => {
    if (!originName.trim() || !destinationName.trim()) return;
    try {
      const wanted = laneMatchKey(originName, destinationName);
      // Deliberately NOT scoped by client. `addresses.client_id` is nullable —
      // CSV-imported and geocode-cache lanes carry none — and the BE filters
      // with `inArray`, which never matches NULL, so scoping would hide exactly
      // the lanes ops bulk-loaded. The name pair is globally unique
      // (`addresses_name_key_key`), so it identifies the lane on its own.
      //
      // `search` is a broad ILIKE over both names and both addresses, so it
      // over-fetches; the exact pair is picked out below.
      //
      // Destination first: on Import every lane shares the same origin (the
      // port), so searching the origin returns the client's whole lane book and
      // the wanted pair can fall off the page. Export inverts that, hence the
      // origin retry.
      const findLane = async (term: string) => {
        const { addresses } = await addressesAPI.list({
          search: term.trim(),
          limit: 100,
        });
        return addresses.find(
          (a) => laneMatchKey(a.originName, a.destinationName) === wanted
        );
      };
      const lane =
        (await findLane(destinationName)) ?? (await findLane(originName));
      if (!lane) {
        toast.info('Alamat belum ada di master', {
          description: `Lane "${originName} → ${destinationName}" tidak ditemukan — isi koordinat tiap stop manual.`,
        });
        return;
      }

      setStops((prev) =>
        syncLinkedStops(
          prev.map((stop, i) => {
            if (i === 0) {
              return applyLaneEndpoint(stop, {
                name: lane.originName,
                address: lane.originAddress,
                latitude: lane.originLat,
                longitude: lane.originLong,
              });
            }
            // The customer leg: the last stop not mirroring the origin.
            const isDestination =
              i ===
              prev.reduce(
                (f, x, j) => (j > 0 && !x.linkedToOrigin ? j : f),
                -1
              );
            return isDestination
              ? applyLaneEndpoint(stop, {
                  name: lane.destinationName,
                  address: lane.destinationAddress,
                  latitude: lane.destinationLat,
                  longitude: lane.destinationLong,
                })
              : stop;
          })
        )
      );
      // Clear the "pilih alamat dari daftar" errors the text-only prefill left.
      setErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (key.endsWith('.address') || key.endsWith('.name'))
            delete next[key];
        }
        return next;
      });
      toast.success('Alamat terisi dari master', {
        description: `${lane.originName} → ${lane.destinationName}`,
      });
    } catch {
      // Master unreachable — the text prefill stands, ops fill coordinates.
    }
  };

  // The UJP CSV carries a driver *name* only (no id/code), but the payload needs
  // the rider **id**. Resolve the name against the directory (`GET /v3/drivers`,
  // same DRIVER + ACTIVE/READY_TO_WORK filter the picker uses) and auto-fill it
  // only on an unambiguous match — an exact name hit, or a single result. On no /
  // ambiguous match we leave the picker empty and prime its search with the name
  // so ops just clicks the right one (names aren't unique + the CSV can't
  // disambiguate). Never blocks the import.
  const autoSelectRiderFromName = async (rawName: string) => {
    const name = rawName.trim();
    if (!name) return;
    // Prime the picker's search either way, so the match (or the candidates) show.
    riders.setSearch(name);
    try {
      const match = await findUniqueDriverByName(name);
      if (!match) {
        toast.info('Driver tidak bisa dipilih otomatis', {
          description: `Pilih driver "${name}" secara manual di daftar.`,
        });
        return;
      }
      setRiderId(String(match.id));
      setRiderCode(match.code ?? '');
      clearError('riderId');
      patchRoute({ driverName: riderDisplayName(match) });
      setRiderPhone(riderPhoneOf(match));
    } catch {
      // Directory lookup failed — leave the picker for a manual pick.
    }
  };

  // Import an approved UJP CSV → prefill Step-1. One row = one shipment; only the
  // first data row is used. Client is not in the export, so it stays a manual pick.
  const handleUjpFile = async (file: File) => {
    try {
      const rows = parseCsv(await file.text());
      if (!rows.length) {
        toast.error('CSV UJP kosong atau tidak valid');
        return;
      }
      const missing = UJP_REQUIRED_HEADERS.filter((h) => !(h in rows[0]));
      if (missing.length) {
        toast.error('Kolom UJP tidak dikenali', {
          description: `Kolom hilang: ${missing.join(', ')}`,
        });
        return;
      }
      const r = rows[0];
      const dt = mapUjpDeliveryType(r.tipe_pengiriman ?? '');
      setBookingId(r.order_no ?? '');
      setDeliveryDate(parseUjpDate(r.tgl_delivery ?? ''));
      setDeliveryType(dt);
      // Fresh stops for the delivery shape, prefilling origin (first PICKUP)
      // and destination. Coordinates still come from the map picker.
      //
      // The destination is the CUSTOMER, so it lands on the last stop that is
      // not mirroring the origin — on Multi Drop that is the middle UNLOAD leg,
      // not the container return, which the origin fills in for us.
      const nextStops = prefillEndpoints(
        makeStops(dt),
        r.origin ?? '',
        r.destination ?? ''
      );
      setStops(applyDefaultWorkflows(syncLinkedStops(nextStops), workflows, exim));
      setRoutePlanId(null);
      setRoute((prev) => ({
        ...prev,
        originName: r.origin ?? '',
        destinationName: r.destination ?? '',
        km: r.km ?? '',
        plateNumber: r.plat_nomor ?? '',
        unitType: r.jenis_unit ?? '',
        driverName: r.driver ?? '',
        serviceType: r.tipe_layanan ?? '',
        shift: r.shift ?? '',
      }));
      setImportedFromUjp(true);
      setManualMode(false);
      setUjpFileName(file.name);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.source;
        return next;
      });
      toast.success('UJP diimport', {
        description:
          rows.length > 1
            ? `${file.name} — 1 dari ${rows.length} baris dipakai`
            : file.name,
      });
      // Best-effort: resolve the CSV driver name to a Driver ID for the picker.
      void autoSelectRiderFromName(r.driver ?? '');
      // Best-effort: upgrade the text-only stop prefill to real coordinates.
      void applyLaneFromUjp(r.origin ?? '', r.destination ?? '');
    } catch {
      toast.error('Gagal membaca CSV UJP');
    }
  };

  // ── Validation ──────────────────────────────────────────────────────────────

  const canProceedSource = manualMode || importedFromUjp;

  const validateStep1 = (): boolean => {
    const next: Record<string, string> = {};
    if (!canProceedSource) {
      next.source = 'Import dari UJP atau isi manual dulu';
    }
    if (clientID == null) next.clientID = 'Client wajib dipilih';
    if (!bookingId.trim()) next.bookingId = 'Booking ID / Order No wajib diisi';
    if (!containerType) next.containerType = 'Tipe kontainer wajib dipilih';
    if (!deliveryDate) next.deliveryDate = 'Tanggal delivery wajib diisi';
    if (!riderId.trim()) next.riderId = 'Driver ID wajib diisi';
    // Required additional-note fields for the chosen direction.
    for (const def of fieldDefs) {
      if (def.required && !additionalNotes[def.key]?.trim()) {
        next[`note:${def.key}`] = `${def.label} wajib diisi`;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateStep2 = (): boolean => {
    const next: Record<string, string> = {};
    for (const stop of stops) {
      if (!stop.name.trim()) next[`${stop.uid}.name`] = 'Nama stop wajib diisi';
      if (!stop.address.trim())
        next[`${stop.uid}.address`] = 'Alamat wajib diisi';
      // lat/long only exist when the user picked a place from the autocomplete —
      // a hand-typed address has no coordinates and the BE requires them.
      else if (stop.latitude == null || stop.longitude == null)
        next[`${stop.uid}.address`] =
          'Pilih alamat dari daftar (butuh koordinat)';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (step === 1) {
      if (!validateStep1()) {
        toast.error('Please fix the highlighted fields');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!validateStep2()) {
        toast.error('Please fix the highlighted fields');
        return;
      }
      setStep(3);
    }
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const buildPayload = (): CreateDirect4WPayload => {
    const kmNum = route.km.trim() === '' ? null : Number(route.km);
    // Only send non-empty note values, trimmed. Server validates required ones
    // for `exim` and freezes each with its label. Ad-hoc custom notes (name →
    // value) merge in under the typed name as the key; a defined field with the
    // same key still wins (spread order), so the two never clash.
    const trimmedNotes = Object.fromEntries(
      [
        ...customNotes.map(
          (n) => [n.name.trim(), n.value.trim()] as const
        ),
        ...Object.entries(additionalNotes).map(
          ([key, value]) => [key, value.trim()] as const
        ),
      ].filter(([key, value]) => key !== '' && value !== '')
    );
    return {
      type: 'DIRECT_4W',
      bookingId: bookingId.trim(),
      clientID: clientID!,
      deliveryType,
      containerType,
      exim,
      ...(Object.keys(trimmedNotes).length > 0
        ? {
            additionalNotes: trimmedNotes,
            // Human labels for the client's known fields, so the BE freezes a
            // real label (not the snake_case key) and every detail view can
            // render `note.label` as-is. Ad-hoc notes are keyed by the name
            // ops typed, so they need no separate label.
            additionalNoteLabels: Object.fromEntries(
              fieldDefs
                .filter((d) => d.key in trimmedNotes)
                .map((d) => [d.key, d.label] as const)
            ),
          }
        : {}),
      deliveryDate: deliveryDate ? toISODate(deliveryDate) : '',
      importedFromUjp,
      rider: {
        id: riderId.trim(),
        code: riderCode.trim() || undefined,
        name: route.driverName.trim() || undefined,
        phoneNumber: riderPhone.trim() || undefined,
      },
      route: {
        originName: route.originName.trim(),
        destinationName: route.destinationName.trim(),
        km: kmNum != null && Number.isFinite(kmNum) ? kmNum : null,
        plateNumber: route.plateNumber.trim(),
        unitType: route.unitType.trim(),
        driverName: route.driverName.trim(),
        serviceType: route.serviceType.trim(),
        shift: route.shift.trim(),
      },
      // latitude/longitude are guaranteed by validateStep2 (place must be
      // picked from the autocomplete) before submit is reachable.
      stops: stops.map((s) => ({
        type: s.type,
        // CR-6: UNLOAD / RETURN_CONTAINER only — a GENERIC drop (and any
        // pickup) goes without `intent`, the BE DTO knows no other values.
        intent: shipmentStopIntent(s),
        name: s.name.trim(),
        address: s.address.trim(),
        latitude: s.latitude!,
        longitude: s.longitude!,
        workflowId: s.workflowId || undefined,
        // CR5-D3: only a plan pin may fall back to the default; a hand pick
        // keeps the server's hard-fail on a stale workflow.
        allowWorkflowFallback: s.workflowFromPlan,
      })),
      ...(routePlanId ? { routePlanId } : {}),
    };
  };

  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await shipmentsAPI.createDirect4W(buildPayload());
      toast.success(
        result.status === 'EXISTS'
          ? 'Shipment 4W sudah ada'
          : 'Shipment 4W dibuat',
        { description: `Waybill ${result.waybill}` }
      );
      // CR5-D3: a plan pin that went stale was swapped for the slot default.
      if (result.workflowFallback?.length) {
        toast.info(
          `${result.workflowFallback.length} workflow stop diganti ke default`,
          {
            description: result.workflowFallback
              .map(
                (f) =>
                  `Workflow stop ${f.seq} diganti ke default (${workflowPinProblemLabel(f.reason)})`
              )
              .join(' · '),
          }
        );
      }
      onCreated();
      onClose();
    } catch (err) {
      toast.error('Gagal membuat shipment', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientOptions = useMemo(
    () => providers.options.map((p) => ({ value: p.id, label: p.name })),
    [providers.options]
  );

  // The shared rider step reports a partial patch; the name lives on
  // `route.driverName` here (it is also what the payload sends as driverName).
  const riderValue: RiderValue = {
    id: riderId,
    code: riderCode,
    name: route.driverName,
    phone: riderPhone,
  };
  const handleRiderChange = (patch: Partial<RiderValue>) => {
    if (patch.id !== undefined) {
      setRiderId(patch.id);
      clearError('riderId');
    }
    if (patch.code !== undefined) setRiderCode(patch.code);
    if (patch.name !== undefined) patchRoute({ driverName: patch.name });
    if (patch.phone !== undefined) setRiderPhone(patch.phone);
  };

  const containerLabel = useMemo(
    () =>
      CONTAINER_OPTIONS.find((c) => c.value === containerType)?.label ?? '—',
    [containerType]
  );

  // Route preview (Review step): plot points + how many stops have coordinates.
  const routePoints = useMemo<RouteStopPoint[]>(
    () =>
      stops.map((s, i) => ({
        id: s.uid,
        sequence: i + 1,
        type: s.type,
        latitude: s.latitude,
        longitude: s.longitude,
      })),
    [stops]
  );
  const locatedCount = useMemo(
    () => stops.filter((s) => s.latitude != null && s.longitude != null).length,
    [stops]
  );

  const clientName = useMemo(
    () => (clientID != null ? providers.labelFor(clientID) : ''),
    [providers, clientID]
  );

  // ── Footer ────────────────────────────────────────────────────────────────

  const footer = (
    <div className="flex items-center justify-between gap-2">
      <div>
        {step > 1 && (
          <Button
            tone="neutral"
            style="stroke"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            <i className="ri-arrow-left-line mr-1.5 text-[14px]" />
            Kembali
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          tone="neutral"
          style="stroke"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Batal
        </Button>
        {step < 3 ? (
          <Button
            onClick={handleNext}
            disabled={step === 1 && !canProceedSource}
          >
            Lanjut
            <i className="ri-arrow-right-line ml-1.5 text-[14px]" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Membuat…' : 'Buat shipment'}
          </Button>
        )}
      </div>
    </div>
  );

  const eyebrow =
    'text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

  return (
    <FormModal
      open={open}
      onClose={onClose}
      icon={<RiAddLine />}
      iconTone="primary"
      title="Buat Shipment — Direct 4W"
      subtitle="Empat-roda exim: tipe, rute & stops, lalu review."
      size="2xl"
      confirmOnClose
      contentRef={bodyRef}
      closeOnBackdropClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      footer={footer}
    >
      <div className="space-y-6">
        <StepIndicator steps={STEPS} currentStep={step} />

        {/* ── STEP 1 — Type + shipment info ───────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Type tabs */}
            <div>
              <p className={`${eyebrow} mb-2`}>Tipe shipment</p>
              <SegmentedControl
                value="DIRECT_4W"
                onValueChange={() => {
                  /* only Direct 4W is enabled in Phase 0 */
                }}
                className="w-full [&>*]:flex-1"
              >
                <SegmentedItem value="DOCKING" disabled>
                  Docking (Soon)
                </SegmentedItem>
                <SegmentedItem value="DIRECT_2W" disabled>
                  Direct 2W (Soon)
                </SegmentedItem>
                <SegmentedItem value="DIRECT_4W">Direct 4W</SegmentedItem>
              </SegmentedControl>
            </div>

            {/* UJP import gate */}
            <div className="rounded-xl border border-stroke-soft-200 bg-bg-weak-50 p-4">
              {/* Native UJP is authoritative for onboarded clients; the CSV
                  round-trip stays only as a transition path. */}
              <Alert
                status="warning"
                appearance="lighter"
                size="sm"
                className="mb-3"
                // The kit's sm alert renders `title ?? children`, so the whole
                // sentence rides on `title`.
                title="Import CSV UJP akan dihentikan. Gunakan menu UJP untuk pengajuan baru."
                action={
                  <a href="/ujp" className="font-medium underline underline-offset-2">
                    Buka menu UJP
                  </a>
                }
              />
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-semibold tracking-tight text-text-strong-950">
                    <i className="ri-lock-2-line text-[15px] text-accent" />
                    Import dari UJP (Running Cost)
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed text-text-sub-600">
                    Biaya &amp; rute berasal dari UJP dengan 2 lapis approval:{' '}
                    <strong>Ops → Head Ops → Finance</strong>. Setelah
                    disetujui, data rute &amp; kendaraan terisi otomatis.
                  </p>
                  {importedFromUjp ? (
                    <p className="mt-1 flex items-center gap-1.5 text-[12px] font-medium text-success-base">
                      <i className="ri-checkbox-circle-fill text-[14px]" />
                      Terisi dari {ujpFileName || 'CSV UJP'} — cek &amp; edit
                      bila perlu.
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-text-soft-400">
                      Upload CSV UJP (kolom: order_no, tgl_delivery,
                      tipe_pengiriman, tipe_layanan, origin, destination, km,
                      plat_nomor, jenis_unit, driver, shift).
                    </p>
                  )}
                </div>
                <input
                  ref={ujpInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUjpFile(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  tone={importedFromUjp ? 'neutral' : 'primary'}
                  style="stroke"
                  size="sm"
                  onClick={() => ujpInputRef.current?.click()}
                  aria-label="Import dari UJP"
                >
                  <i className="ri-download-cloud-2-line mr-1.5 text-[14px]" />
                  {importedFromUjp ? 'Ganti file UJP' : 'Import dari UJP'}
                </Button>
              </div>

              <label className="mt-3 flex cursor-pointer items-center gap-2 border-t border-stroke-soft-200 pt-3 text-[13px] text-text-sub-600">
                <input
                  type="checkbox"
                  checked={manualMode}
                  onChange={(e) => {
                    setManualMode(e.target.checked);
                    clearError('source');
                  }}
                  className="size-4 rounded-gsm accent-accent"
                />
                Isi manual (tanpa UJP)
              </label>
              {errors.source && <Hint tone="error">{errors.source}</Hint>}
            </div>

            {/* Core fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field>
                <Label htmlFor="s4w-client" required>
                  Client
                </Label>
                <SearchSelect
                  options={clientOptions}
                  value={clientID ?? undefined}
                  selectedLabelFallback={
                    clientID != null ? providers.labelFor(clientID) : undefined
                  }
                  onChange={(value) => {
                    setClientID(value ? Number(value) : null);
                    clearError('clientID');
                  }}
                  placeholder="Cari / pilih client…"
                  error={!!errors.clientID}
                  loading={providers.loading}
                  hasMore={providers.hasMore}
                  disableLocalFilter
                  onSearchChange={providers.setSearch}
                  onLoadMore={providers.loadMore}
                  emptyMessage={providers.error ?? 'Client tidak ditemukan'}
                />
                {errors.clientID && <Hint tone="error">{errors.clientID}</Hint>}
              </Field>

              <Field>
                <Label htmlFor="s4w-booking" required>
                  Booking ID / Order No
                </Label>
                <InputRoot size="md" invalid={!!errors.bookingId}>
                  <Input
                    id="s4w-booking"
                    value={bookingId}
                    placeholder="mis. BK-2026-00123"
                    onChange={(e) => {
                      setBookingId(e.target.value);
                      clearError('bookingId');
                    }}
                  />
                </InputRoot>
                {errors.bookingId && (
                  <Hint tone="error">{errors.bookingId}</Hint>
                )}
              </Field>
            </div>

            <Field>
              <Label htmlFor="s4w-delivery-type" required>
                Tipe pengiriman
              </Label>
              <SegmentedControl
                value={deliveryType}
                onValueChange={(v: string) => {
                  if (v) handleDeliveryTypeChange(v as Direct4WDeliveryType);
                }}
                className="w-full [&>*]:flex-1"
              >
                {DELIVERY_TYPES.map((d) => (
                  <SegmentedItem key={d.value} value={d.value}>
                    {d.label}
                  </SegmentedItem>
                ))}
              </SegmentedControl>
              <Hint>
                Menentukan jumlah &amp; jenis stop di langkah berikut.
              </Hint>
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field>
                <Label htmlFor="s4w-date" required>
                  Tanggal delivery
                </Label>
                <DatePicker
                  value={deliveryDate}
                  onValueChange={(d) => {
                    setDeliveryDate(d);
                    clearError('deliveryDate');
                  }}
                  placeholder="Pilih tanggal…"
                  size="lg"
                  triggerClassName="rounded-lg"
                />
                {errors.deliveryDate && (
                  <Hint tone="error">{errors.deliveryDate}</Hint>
                )}
              </Field>

              <Field>
                <Label htmlFor="s4w-container" required>
                  Tipe kontainer
                </Label>
                <Select
                  value={containerType}
                  onValueChange={(value) => {
                    setContainerType(value);
                    clearError('containerType');
                  }}
                >
                  <SelectTrigger id="s4w-container" aria-label="Tipe kontainer">
                    <SelectValue placeholder="Pilih tipe kontainer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTAINER_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.containerType && (
                  <Hint tone="error">{errors.containerType}</Hint>
                )}
              </Field>
            </div>

            {/* Type (EXIM) + catatan tambahan dinamis */}
            <Field>
              <Label htmlFor="s4w-direction" required>
                Type
              </Label>
              <SegmentedControl
                value={exim}
                onValueChange={(v: string) => {
                  if (v) setExim(v as ShipmentExim);
                }}
                className="w-full [&>*]:flex-1"
              >
                {DIRECTION_OPTIONS.map((d) => (
                  <SegmentedItem key={d.value} value={d.value}>
                    {d.label}
                  </SegmentedItem>
                ))}
              </SegmentedControl>
              <Hint>
                Export / Import menentukan kolom recon wajib &amp; workflow default
                per type. Umum = non-EXIM.
              </Hint>
            </Field>

            {/* Additional notes — one section: the client's known fields (FE-
                resolved by client name) plus free-form ad-hoc rows below. */}
            {clientID != null && (
              <div className="space-y-4 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-4 py-4">
                <div className="flex items-center justify-between">
                  <p className={eyebrow}>Catatan tambahan</p>
                  <span className="text-[11px] text-text-sub-600 tabular-nums">
                    {customNotes.length}/{MAX_CUSTOM_NOTES}
                  </span>
                </div>

                {fieldDefs.length > 0 && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {fieldDefs.map((def) => {
                      const errKey = `note:${def.key}`;
                      const value = additionalNotes[def.key] ?? '';
                      const setValue = (v: string) => {
                        setAdditionalNotes((prev) => ({
                          ...prev,
                          [def.key]: v,
                        }));
                        clearError(errKey);
                      };
                      return (
                        <Field key={def.id}>
                          <Label htmlFor={`s4w-note-${def.key}`} required={def.required}>
                            {def.label}
                          </Label>
                          {def.type === 'Date' ? (
                            <DatePicker
                              value={value ? new Date(`${value}T00:00:00`) : undefined}
                              onValueChange={(d) =>
                                setValue(d ? toISODate(d) : '')
                              }
                              placeholder="Pilih tanggal…"
                              size="lg"
                              triggerClassName="rounded-lg"
                            />
                          ) : (
                            <InputRoot size="md">
                              <Input
                                id={`s4w-note-${def.key}`}
                                type={def.type === 'Number' ? 'number' : 'text'}
                                value={value}
                                placeholder={def.description ?? def.label}
                                onChange={(e) => setValue(e.target.value)}
                              />
                            </InputRoot>
                          )}
                          {errors[errKey] ? (
                            <Hint tone="error">{errors[errKey]}</Hint>
                          ) : def.description ? (
                            <Hint>{def.description}</Hint>
                          ) : null}
                        </Field>
                      );
                    })}
                  </div>
                )}

                {/* Ad-hoc free rows — below a light divider when known fields
                    sit above, so it still reads as one section. */}
                <p
                  className={`text-[11px] leading-relaxed text-text-sub-600 ${
                    fieldDefs.length > 0
                      ? 'border-t border-stroke-soft-200 pt-3'
                      : ''
                  }`}
                >
                  Nilai bebas di luar kolom baku (mis. No. Tel Gate, Nomor Seal) —
                  isi nama kolom &amp; nilainya, tersimpan bersama shipment.
                </p>
                {customNotes.map((n) => (
                  <div
                    key={n.id}
                    className="grid grid-cols-[1fr_1fr_auto] items-center gap-2"
                  >
                    <InputRoot size="md">
                      <Input
                        aria-label="Nama kolom"
                        placeholder="Nama kolom"
                        value={n.name}
                        onChange={(e) =>
                          updateCustomNote(n.id, { name: e.target.value })
                        }
                      />
                    </InputRoot>
                    <InputRoot size="md">
                      <Input
                        aria-label="Nilai"
                        placeholder="Nilai"
                        value={n.value}
                        onChange={(e) =>
                          updateCustomNote(n.id, { value: e.target.value })
                        }
                      />
                    </InputRoot>
                    <button
                      type="button"
                      onClick={() => removeCustomNote(n.id)}
                      aria-label="Hapus baris"
                      className="flex size-9 items-center justify-center rounded-lg border border-stroke-soft-200 text-text-sub-600 transition-colors hover:border-error-base hover:text-error-base"
                    >
                      <i className="ri-close-line text-[16px]" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCustomNote}
                  disabled={customNotes.length >= MAX_CUSTOM_NOTES}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-stroke-sub-300 px-3 py-1.5 text-[12px] font-semibold text-text-sub-600 transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className="ri-add-line text-[14px]" /> Tambah kolom
                </button>
              </div>
            )}

            {/* Provenance legend */}
            <div className="flex items-start gap-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
              <i className="ri-information-line mt-px text-[14px] text-text-sub-600" />
              <p className="text-[11px] leading-relaxed text-text-sub-600">
                <span className="font-semibold text-text-strong-950">
                  Field abu-abu
                </span>{' '}
                = terisi dari import Running Cost (UJP) — bisa diedit. Biaya
                &amp; approval tetap di sistem UJP.
              </p>
            </div>

            {/* Rute (dari UJP) */}
            <div className="space-y-4">
              <p className={eyebrow}>Rute (dari UJP)</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field>
                  <Label htmlFor="s4w-origin">Origin</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-origin"
                      value={route.originName}
                      placeholder="Origin"
                      onChange={(e) =>
                        patchRoute({ originName: e.target.value })
                      }
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor="s4w-dest">Destination</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-dest"
                      value={route.destinationName}
                      placeholder="Destination"
                      onChange={(e) =>
                        patchRoute({ destinationName: e.target.value })
                      }
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor="s4w-km">KM</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-km"
                      type="number"
                      min={0}
                      value={route.km}
                      placeholder="0"
                      onChange={(e) => patchRoute({ km: e.target.value })}
                    />
                  </InputRoot>
                </Field>
              </div>
            </div>

            {/* Kendaraan & driver (dari UJP) */}
            <div className="space-y-4">
              <p className={eyebrow}>Kendaraan &amp; driver (dari UJP)</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label htmlFor="s4w-plate">Plat nomor</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-plate"
                      value={route.plateNumber}
                      placeholder="mis. B 1234 XYZ"
                      onChange={(e) =>
                        patchRoute({ plateNumber: e.target.value })
                      }
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor="s4w-unit">Jenis unit</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-unit"
                      value={route.unitType}
                      placeholder="mis. CDD / Fuso"
                      onChange={(e) => patchRoute({ unitType: e.target.value })}
                    />
                  </InputRoot>
                </Field>
                <Direct4WRiderStep
                  value={riderValue}
                  onChange={handleRiderChange}
                  riders={riders}
                  error={errors.riderId}
                  idPrefix="s4w"
                  fieldClassName={GREY_FIELD}
                />
                <Field>
                  <Label htmlFor="s4w-service">Tipe layanan</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-service"
                      value={route.serviceType}
                      placeholder="mis. Reguler"
                      onChange={(e) =>
                        patchRoute({ serviceType: e.target.value })
                      }
                    />
                  </InputRoot>
                </Field>
                <Field>
                  <Label htmlFor="s4w-shift">Shift</Label>
                  <InputRoot size="md" className={GREY_FIELD}>
                    <Input
                      id="s4w-shift"
                      value={route.shift}
                      placeholder="mis. Pagi"
                      onChange={(e) => patchRoute({ shift: e.target.value })}
                    />
                  </InputRoot>
                </Field>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2 — Route & stops (shared step) ───────────────────────── */}
        {step === 2 && (
          <Direct4WStopsStep
            open={open}
            stops={stops}
            onChange={setStops}
            errors={errors}
            onClearError={clearError}
            workflows={workflows}
            workflowsLoading={workflowsLoading}
            wfDetails={wfDetails}
            wfDetailErrors={wfDetailErrors}
            exim={exim}
            hasClient={clientID != null}
            deliveryTypeLabel={
              DELIVERY_TYPES.find((d) => d.value === deliveryType)?.label
            }
            plans={routePlans}
            plansLoading={routePlansLoading}
            chargedPositioning={chargedPositioning}
            onFillFromPlan={fillStopsFromPlan}
          />
        )}

        {/* ── STEP 3 — Review + submit ────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-stroke-soft-200">
              <div className="border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-2.5">
                <p className={eyebrow}>Ringkasan shipment</p>
              </div>
              <dl className="divide-y divide-stroke-soft-200 text-sm">
                <ReviewRow label="Tipe" value="Direct 4W" />
                <ReviewRow
                  label="Client"
                  value={clientName || `#${clientID ?? '—'}`}
                />
                <ReviewRow label="Booking ID" value={bookingId || '—'} mono />
                <ReviewRow
                  label="Tipe pengiriman"
                  value={
                    DELIVERY_TYPES.find((d) => d.value === deliveryType)
                      ?.label ?? '—'
                  }
                />
                <ReviewRow
                  label="Tanggal delivery"
                  value={deliveryDate ? toISODate(deliveryDate) : '—'}
                  mono
                />
                <ReviewRow label="Tipe kontainer" value={containerLabel} />
                <ReviewRow
                  label="Sumber data"
                  value={importedFromUjp ? 'Import UJP' : 'Isi manual'}
                />
              </dl>
            </div>

            <div className="rounded-xl border border-stroke-soft-200">
              <div className="border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-2.5">
                <p className={eyebrow}>Rute &amp; kendaraan</p>
              </div>
              <dl className="divide-y divide-stroke-soft-200 text-sm">
                <ReviewRow
                  label="Origin → Destination"
                  value={`${route.originName || '—'} → ${
                    route.destinationName || '—'
                  }`}
                />
                <ReviewRow label="KM" value={route.km || '—'} mono />
                <ReviewRow
                  label="Plat / Unit"
                  value={`${route.plateNumber || '—'} · ${
                    route.unitType || '—'
                  }`}
                />
                <ReviewRow label="Driver" value={route.driverName || '—'} />
                <ReviewRow
                  label="Layanan / Shift"
                  value={`${route.serviceType || '—'} · ${route.shift || '—'}`}
                />
              </dl>
            </div>

            <div className="rounded-xl border border-stroke-soft-200">
              <div className="flex items-center justify-between border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-2.5">
                <p className={eyebrow}>Rute &amp; stops ({stops.length})</p>
                <span
                  className={`text-[11px] font-semibold tabular-nums ${
                    locatedCount === stops.length
                      ? 'text-emerald-600'
                      : 'text-amber-600'
                  }`}
                >
                  {locatedCount}/{stops.length} ada lokasi
                </span>
              </div>

              {/* Map preview — MapLibre + free CARTO tiles (no Google). */}
              <div className="p-4 pb-0">
                {locatedCount > 0 ? (
                  <RouteStopsMap stops={routePoints} />
                ) : (
                  <div className="flex h-[240px] items-center justify-center rounded-xl border border-dashed border-stroke-soft-200 bg-bg-weak-50 px-4 text-center text-[12px] text-text-soft-400">
                    Belum ada stop dengan koordinat — isi alamat tiap stop
                    (pilih saran untuk auto-isi koordinat) untuk melihat peta.
                  </div>
                )}
                {locatedCount > 0 && (
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] text-text-soft-400">
                    <i className="ri-route-line text-[13px]" />
                    Perkiraan rute jalan mengikuti urutan stop — rute final
                    dihitung setelah shipment dibuat.
                  </p>
                )}
                {locatedCount > 0 && locatedCount < stops.length && (
                  <p className="mt-1 text-[11px] text-amber-600">
                    {stops.length - locatedCount} stop belum ada koordinat —
                    tidak tampil di peta.
                  </p>
                )}
              </div>

              {/* Completeness list — doubles as a pre-submit checklist. */}
              <ul className="divide-y divide-stroke-soft-200">
                {stops.map((stop, index) => {
                  const summary = workflows[stop.type].find(
                    (w) => w.id === stop.workflowId
                  );
                  const hasCoords =
                    stop.latitude != null && stop.longitude != null;
                  return (
                    <li key={stop.uid} className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] font-semibold text-accent tabular-nums">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="inline-flex items-center rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 px-2 py-0.5 text-[11px] font-semibold text-text-sub-600">
                          {driverLabel(stop)}
                        </span>
                        <span className="text-[13px] font-medium tracking-tight text-text-strong-950">
                          {stop.name || '—'}
                        </span>
                      </div>
                      <p className="mt-1 text-[12px] text-text-sub-600">
                        {stop.address || '—'}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-medium">
                        <span
                          className={
                            hasCoords ? 'text-emerald-600' : 'text-amber-600'
                          }
                        >
                          <i
                            className={`mr-1 ${
                              hasCoords
                                ? 'ri-map-pin-2-fill'
                                : 'ri-map-pin-line'
                            }`}
                          />
                          {hasCoords ? 'Koordinat siap' : 'Koordinat belum'}
                        </span>
                        <span
                          className={
                            summary ? 'text-emerald-600' : 'text-text-soft-400'
                          }
                        >
                          <i
                            className={`mr-1 ${
                              summary ? 'ri-flow-chart' : 'ri-subtract-line'
                            }`}
                          />
                          {summary ? summary.name : 'Workflow —'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};

const ReviewRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
}> = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-4 px-4 py-2.5">
    <dt className="text-[12px] text-text-sub-600">{label}</dt>
    <dd
      className={`text-right text-[13px] text-text-strong-950 ${
        mono ? 'font-mono tabular-nums' : 'tracking-tight'
      }`}
    >
      {value}
    </dd>
  </div>
);

export default CreateShipment4WModal;
